import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined"
import CloseIcon from "@mui/icons-material/Close"
import LoadingButton from "@mui/lab/LoadingButton"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import { useRef, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { ALLOWED_SIGNATURE_IMAGE_TYPES } from "../components/SignatureImageUpload"
import {
  MY_MLS_BOOST_CAMPAIGN_MEDIA_API_PATH,
  mergeBoostCampaignMediaIntoCriteria,
  normalizeBoostCampaignCriteria,
  saveBoostCampaignCriteriaLocal,
} from "./boostCampaignCriteria"

const MEDIA_VALIDATION_MESSAGE =
  "Only JPEG, PNG, GIF, and WebP images are allowed (max 10 MB)."

function validateMediaFile(file) {
  if (!ALLOWED_SIGNATURE_IMAGE_TYPES.includes(file.type)) {
    throw new Error(MEDIA_VALIDATION_MESSAGE)
  }
  if (file.size > 10 * 1024 * 1024) {
    throw new Error(MEDIA_VALIDATION_MESSAGE)
  }
}

/**
 * Upload reference media for Lead Targeting (Airtable: Additional Media).
 */
export default function BoostCampaignMediaUpload({
  additionalMediaUrl = "",
  disabled = false,
  fieldError = null,
  onMediaChanged,
  onBusyChange,
  criteriaSnapshot = null,
}) {
  const axios = useAuthorizedAxiosClient()
  const fileInputRef = useRef()
  const [isUploading, setIsUploading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [error, setError] = useState(null)

  const displayError = fieldError || error
  const busy = isUploading || isRemoving

  function notifyBusy(nextBusy) {
    onBusyChange?.(nextBusy)
  }

  function applyMediaResponse(data) {
    const mediaCriteria = normalizeBoostCampaignCriteria(data?.criteria)
    const merged = mergeBoostCampaignMediaIntoCriteria(
      criteriaSnapshot ?? { additionalMediaUrl },
      mediaCriteria
    )
    saveBoostCampaignCriteriaLocal(merged)
    onMediaChanged?.({
      criteria: merged,
      updatedAt: data?.updated_at || data?.criteria?.updatedAt || null,
    })
  }

  function handleUploadButtonClicked() {
    fileInputRef.current?.click()
  }

  async function handleFileInputChanged(event) {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file) {
      return
    }

    try {
      validateMediaFile(file)
      setError(null)
    } catch (validationError) {
      setError(validationError.message)
      return
    }

    const formData = new FormData()
    formData.append("file", file)
    setIsUploading(true)
    notifyBusy(true)
    try {
      const { data } = await axios.post(
        MY_MLS_BOOST_CAMPAIGN_MEDIA_API_PATH,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      applyMediaResponse(data)
    } catch (err) {
      const apiError = err.response?.data?.field_errors?.file
      setError(apiError || err.response?.data?.detail || "Upload failed.")
    } finally {
      setIsUploading(false)
      notifyBusy(false)
    }
  }

  async function handleRemoveClicked() {
    setIsRemoving(true)
    notifyBusy(true)
    setError(null)
    try {
      const { data } = await axios.delete(MY_MLS_BOOST_CAMPAIGN_MEDIA_API_PATH)
      applyMediaResponse(data)
    } catch (err) {
      setError(err.response?.data?.detail || "Could not remove image.")
    } finally {
      setIsRemoving(false)
      notifyBusy(false)
    }
  }

  return (
    <Column
      sx={{
        gap: 1,
        p: 1.5,
        borderRadius: 2,
        border: "1px dashed",
        borderColor: displayError ? "error.main" : "divider",
        bgcolor: "action.hover",
      }}
    >
      <Row
        sx={{
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Column sx={{ gap: 0.25, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Upload media set
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Optional reference image for your targeting notes. JPEG, PNG, GIF,
            or WebP up to 10 MB.
          </Typography>
        </Column>
        <LoadingButton
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<AddPhotoAlternateOutlinedIcon />}
          disabled={disabled || busy}
          loading={isUploading}
          onClick={handleUploadButtonClicked}
          sx={{ flexShrink: 0 }}
        >
          {additionalMediaUrl ? "Replace" : "Upload"}
        </LoadingButton>
      </Row>

      {additionalMediaUrl ? (
        <Box sx={{ position: "relative", alignSelf: "flex-start" }}>
          <Box
            component="img"
            src={additionalMediaUrl}
            alt="Lead targeting reference"
            sx={{
              display: "block",
              maxWidth: "100%",
              maxHeight: 220,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          />
          <IconButton
            size="small"
            aria-label="Remove uploaded media"
            disabled={disabled || busy}
            onClick={handleRemoveClicked}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              bgcolor: "background.paper",
              "&:hover": { bgcolor: "background.paper" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : null}

      {displayError ? (
        <Typography variant="caption" color="error">
          {displayError}
        </Typography>
      ) : null}

      {additionalMediaUrl ? (
        <Button
          variant="text"
          color="inherit"
          size="small"
          disabled={disabled || busy}
          onClick={handleRemoveClicked}
          sx={{ alignSelf: "flex-start", px: 0 }}
        >
          Remove image
        </Button>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_SIGNATURE_IMAGE_TYPES.join(",")}
        onChange={handleFileInputChanged}
        style={{ display: "none" }}
      />
    </Column>
  )
}
